import { useCallback } from 'react';

import { logout as submitLogout } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isReactNativeWebView } from '@rover/utilities/fireEmbeddedWebEvent';
import { redirect } from '@rover/utilities/url';

import type { VerificationExit, VerificationExitOptions } from './useVerificationExit.common';

/**
 * Tells a native WebView host to clear its own auth state. Logging out of the web
 * session only drops the cookie inside the WebView, so without this the embedded
 * app stays signed in. The host matches on `type`, not the `action` shape that
 * `fireEmbeddedWebEvent` sends, so the message is posted directly.
 */
const notifyNativeHostOfLogout = (): void => {
  if (!isReactNativeWebView()) {
    return;
  }

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: 'logout_requested',
      source: 'email_verification',
    })
  );
};

const useVerificationExit = ({
  returnUrl,
  nextUrl,
  onExit,
}: VerificationExitOptions = {}): VerificationExit => {
  const exit = useCallback(() => {
    // A host that dismisses the flow itself must not also be navigated away from,
    // which would tear down the page the modal was covering.
    if (onExit) {
      onExit();
      return;
    }

    redirect(returnUrl || '/');
  }, [onExit, returnUrl]);

  const logout = useCallback(() => {
    // Notify the host before logging out: the logout navigates away and tears this
    // page down, which is also what closes the verification modal in the app.
    notifyNativeHostOfLogout();

    // Django posts an empty `next` on logout, letting the logout view pick the
    // destination, so an absent `nextUrl` must stay empty rather than become '/'.
    submitLogout({ nextLocation: nextUrl ?? '' });
  }, [nextUrl]);

  return { exit, logout };
};

export default useVerificationExit;
